import { BlockStack, Card, InlineStack, Text } from "@shopify/polaris";

import { t } from "../lib/i18n";
import { planLabel } from "../lib/plan";
import type { Usage } from "../types";

interface UsageCardProps {
  title: string;
  usage: Usage;
  /** 件数の単位。アーカイブは「件」（注文）、発行は「枚」（書類）。 */
  unit: "件" | "枚";
  /**
   * 上限超過でその機能が**実際に止まるか**。
   *
   * 🔴 アーカイブは `false`＝電帳法は全件保存が義務で、上限を超えても取り込みは続く
   * （止まるのは一括 ZIP と CSV 索引簿だけ）。発行は `true`＝次の1枚が 402 で止まる。
   * ここを両方 `true` で描くと「保存が止まった」という嘘を店に見せることになる。
   */
  blocksOnOverLimit: boolean;
}

export function UsageCard({ title, usage, unit, blocksOnOverLimit }: UsageCardProps) {
  // 原文をキーにするので、単位ごとに別の文を渡す（`{count} 件` と `{count} 枚` を
  // 動的に組み立てると辞書が引けなくなる）。
  const countLabel =
    unit === "件"
      ? t("{count} 件", { count: usage.count })
      : t("{count} 枚", { count: usage.count });
  const limitLabel =
    usage.limit === null
      ? t("無制限")
      : unit === "件"
        ? t("上限 {limit} 件", { limit: usage.limit })
        : t("上限 {limit} 枚", { limit: usage.limit });

  return (
    <Card>
      <BlockStack gap="200">
        <Text as="h2" variant="headingSm">
          {title}
        </Text>
        <InlineStack gap="200" blockAlign="baseline">
          <Text as="p" variant="heading2xl">
            {countLabel}
          </Text>
          <Text as="span" tone="subdued">
            {limitLabel}
          </Text>
        </InlineStack>
        <UsageNotice usage={usage} blocksOnOverLimit={blocksOnOverLimit} />
      </BlockStack>
    </Card>
  );
}

/** 上限まわりの案内。超過・接近・通常で文言が変わる。 */
function UsageNotice({
  usage,
  blocksOnOverLimit,
}: Pick<UsageCardProps, "usage" | "blocksOnOverLimit">) {
  const plan = planLabel(usage.required_plan_code);

  if (usage.over_limit && blocksOnOverLimit) {
    return (
      <BlockStack gap="100">
        <Text as="p" tone="critical">
          {t("上限に達しています。次の1枚は発行できません")}
        </Text>
        <Text as="p" tone="subdued">
          {t("{plan} 以上のプランで発行を再開できます", { plan })}
        </Text>
      </BlockStack>
    );
  }

  if (usage.over_limit) {
    return (
      <BlockStack gap="100">
        <Text as="p" tone="caution">
          {t("上限を超えていますが、保存は止まっていません")}
        </Text>
        <Text as="p" tone="subdued">
          {t(
            "電子帳簿保存法は全件保存が義務のため、上限を超えても取り込みは続きます。まとめて取り出す際に {plan} 以上のプランが必要です。",
            { plan },
          )}
        </Text>
      </BlockStack>
    );
  }

  if (usage.approaching_limit) {
    return (
      <Text as="p" tone="caution">
        {t("上限に近づいています")}
      </Text>
    );
  }

  return null;
}
